import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Button } from "primereact/button";

import EntityFormPage from "../../../../components/EntityFormPage/EntityFormPage";
import StationFormFields from "./StationFormFields";
import { useSubStationInputs } from "./useSubStationInputs";
import { buildStationPayload } from "./stationFormUtils";
import { ToastContext } from "../../../../App";
import {
  useCreateStationMutation,
  useLazyGetStationDataQuery,
  useUploadImageMutation,
  useGetStationListQuery,
} from "../../../../redux/services/foodStation";
import uploadSubStationImageHelper from "../../../../utils/substationImageUploader";
import { getImageUrl } from "../../../../utils/getImageUrl";
import { parseSubStations } from "../../../../utils/stationUtils";
import getSubdomain from "../../../../utils/getSubdomain";

import * as styles from "../Stations.module.css";

const LIST_PATH = "/app/manager/stations";

function StationCreatePage() {
  const showToast = useContext(ToastContext);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      title: "",
      midnight_autocomplete: true,
      is_multi: false,
      info: "",
      baseline_time: 6,
      restriction: 10,
      order_max_threshold: "",
      order_min_threshold: "",
    },
  });

  const isMulti = watch("is_multi");

  const subStationState = useSubStationInputs();
  const { subStationInputs, setSubStationInputs, setIsMulti } = subStationState;

  useEffect(() => {
    setIsMulti(isMulti);
  }, [isMulti, setIsMulti]);

  const [
    createStation,
    {
      isError: isCreateStationError,
      error: createStationError,
    },
  ] = useCreateStationMutation();
  const [getStationData] = useLazyGetStationDataQuery();
  const [uploadImage] = useUploadImageMutation();
  const { refetch: refetchStations } = useGetStationListQuery(getSubdomain());

  useEffect(() => {
    if (isCreateStationError) {
      showToast(
        "error",
        createStationError?.status,
        createStationError?.data?.message ||
          (createStationError?.data?.errors && "Incorrect data"),
      );
      setIsSubmitting(false);
    }
  }, [isCreateStationError]);

  const uploadSubStationImage = (subStationId, file) =>
    uploadSubStationImageHelper({
      subStationId,
      file,
      uploadImage,
      setSubStationInputs,
      refetchStations,
      getImageUrl,
      showToast,
    });

  const onCreate = async (data) => {
    if (isSubmitting) return;

    const subStations = data.is_multi ? parseSubStations(subStationInputs) : [];
    if (data.is_multi && subStations.length < 2) {
      showToast(
        "error",
        "Sub-stations",
        "For multi station, add at least 2 sub-station names",
      );
      return;
    }

    const payload = { ...buildStationPayload(data, subStations), active: false };

    try {
      setIsSubmitting(true);
      const res = await createStation({
        subdomain: getSubdomain(),
        data: payload,
      }).unwrap();

      let createdSubs = res?.sub_stations || [];
      if ((!createdSubs || createdSubs.length === 0) && res?.id) {
        try {
          const full = await getStationData({
            id: res.id,
            subdomain: getSubdomain(),
          }).unwrap();
          createdSubs = full?.sub_stations || [];
        } catch (e) {
          showToast(
            "error",
            "Station",
            "Created station returned no sub-stations; image uploads skipped",
          );
          createdSubs = [];
        }
      }
      const uploadPromises = [];
      if (createdSubs.length > 0 && subStationInputs?.length > 0) {
        for (let i = 0; i < createdSubs.length; i++) {
          const cs = createdSubs[i];
          let match = subStationInputs.find(
            (s) => s.name && s.name.trim() === (cs.name || "").trim(),
          );
          if (!match) {
            match = subStationInputs[i];
          }
          if (match && match.imageFile) {
            uploadPromises.push(uploadSubStationImage(cs.id, match.imageFile));
          }
        }
      }
      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
      }
      try {
        await refetchStations();
      } catch (e) {
        showToast(
          "error",
          "Station",
          "Unable to refresh station data after create",
        );
      }
      showToast("success", "Station", "Station created!");
      navigate(LIST_PATH);
    } catch (err) {
      // error handling is covered by existing effects
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <EntityFormPage
      title="New Food Station"
      backTo={LIST_PATH}
      footer={
        <Button
          label="Save"
          type="submit"
          disabled={isSubmitting}
          className={`p-button-success ${styles.saveBtn}`}
          onClick={handleSubmit(onCreate)}
        />
      }
    >
      <form onSubmit={handleSubmit(onCreate)}>
        <StationFormFields control={control} isMulti={isMulti} {...subStationState} />
        <button type="submit" hidden />
      </form>
    </EntityFormPage>
  );
}

export default StationCreatePage;
